import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from '@tanstack/react-router';
import { Bell, ChevronDown, KeyRound, LogOut, Menu, Moon, Search, ShieldCheck, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChangePasswordModal } from '@/components/ui/change-password-modal';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';

const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: '超级管理员',
  TENANT_ADMIN: '租户管理员',
  TENANT_MEMBER: '租户成员',
};

const ROLE_COLOR: Record<string, string> = {
  SUPER_ADMIN: 'bg-danger/10 text-danger',
  TENANT_ADMIN: 'bg-brand-100 text-brand-700',
  TENANT_MEMBER: 'bg-neutral-100 text-neutral-700',
};

export function TopBar() {
  const { darkMode, toggleDark, toggleDrawer } = useAppStore();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const initial = user?.display_name?.[0] ?? user?.username?.[0] ?? 'G';
  const roleLabel = user ? ROLE_LABEL[user.role] ?? user.role : '';
  const roleColor = user ? ROLE_COLOR[user.role] ?? 'bg-neutral-100 text-neutral-700' : '';

  function handleLogout() {
    clearAuth();
    navigate({ to: '/login', search: { redirect: undefined } });
  }

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-outline-variant/20 bg-surface/80 px-4 backdrop-blur">
      <Button variant="ghost" size="icon" onClick={toggleDrawer} aria-label="切换侧栏">
        <Menu className="h-5 w-5" />
      </Button>
      <a href="/" className="flex items-center gap-2">
        <img src="/StarFox_logo_2.png" alt="StarFox" className="h-8" />
        <span className="ml-2 hidden text-xs text-neutral-500 md:inline">短剧 AI 创作平台</span>
      </a>
      <div className="ml-6 hidden flex-1 max-w-md md:flex">
        <div className="relative w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-outline" />
          <input
            type="search"
            placeholder="搜索剧本 / 角色 / 工单 ⌘K"
            className="h-9 w-full rounded-full border border-outline-variant/40 bg-surface-variant pl-9 pr-3 text-sm placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
      </div>
      <div className="ml-auto flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={toggleDark} aria-label="切换暗色">
          {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
        <Button variant="ghost" size="icon" aria-label="通知">
          <Bell className="h-5 w-5" />
        </Button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="ml-2 flex items-center gap-2 rounded-full border border-transparent px-1.5 py-1 transition hover:border-outline-variant/40 hover:bg-surface-variant"
              aria-label="账号菜单"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-600">
                {initial}
              </div>
              <div className="hidden text-left md:block">
                <div className="text-xs font-medium leading-tight text-neutral-900">
                  {user?.display_name ?? '未登录'}
                </div>
                {roleLabel && (
                  <div className={`mt-0.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] leading-none ${roleColor}`}>
                    {roleLabel}
                  </div>
                )}
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-[100] min-w-[200px] rounded-xl border border-outline-variant/30 bg-surface p-1 shadow-m3"
            >
              <div className="px-3 py-2">
                <div className="text-sm font-medium text-neutral-900">{user?.display_name}</div>
                <div className="mt-0.5 text-xs text-neutral-500">@{user?.username}</div>
                {user?.tenant_id && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-600">
                    <ShieldCheck className="h-3 w-3" /> {user.tenant_id}
                  </div>
                )}
              </div>
              <DropdownMenu.Separator className="my-1 h-px bg-outline-variant/30" />
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 outline-none data-[highlighted]:bg-surface-variant"
                onSelect={(e) => {
                  e.preventDefault();
                  setPwOpen(true);
                }}
              >
                <KeyRound className="h-4 w-4" />
                修改密码
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 outline-none data-[highlighted]:bg-danger/10 data-[highlighted]:text-danger"
                onSelect={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                退出登录
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}
